import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { apiClient } from "@/lib/api";
import { Trophy, DollarSign, Users, Star, Globe, ChevronLeft, ChevronRight, RefreshCw, TrendingUp } from "lucide-react";

// Country flag mapping for display
const COUNTRY_FLAGS = {
  "Canada": "🇨🇦", "USA": "🇺🇸", "India": "🇮🇳", "UK": "🇬🇧", "UAE": "🇦🇪",
  "Singapore": "🇸🇬", "Australia": "🇦🇺", "Italy": "🇮🇹", "Nigeria": "🇳🇬", 
  "South Africa": "🇿🇦", "SA": "🇿🇦", "Mexico": "🇲🇽", "MX": "🇲🇽", "Brazil": "🇧🇷",
  "Japan": "🇯🇵", "China": "🇨🇳", "Germany": "🇩🇪", "Kenya": "🇰🇪", "France": "🇫🇷",
  "Egypt": "🇪🇬", "S. Korea": "🇰🇷", "Spain": "🇪🇸", "Russia": "🇷🇺", 
  "Hong Kong": "🇭🇰", "Netherlands": "🇳🇱", "NL": "🇳🇱", "Pakistan": "🇵🇰",
  "AU": "🇦🇺", "Turkey": "🇹🇷", "Ghana": "🇬🇭", "Ireland": "🇮🇪", "Iran": "🇮🇷",
  "Greece": "🇬🇷", "Sweden": "🇸🇪", "Argentina": "🇦🇷", "AR": "🇦🇷", 
  "Malaysia": "🇲🇾", "MY": "🇲🇾", "Switzerland": "🇨🇭", "CH": "🇨🇭", 
  "Denmark": "🇩🇰", "DK": "🇩🇰", "New Zealand": "🇳🇿", "NZ": "🇳🇿", 
  "Morocco": "🇲🇦", "MA": "🇲🇦", "Czech Republic": "🇨🇿", "CZ": "🇨🇿",
  "Peru": "🇵🇪", "Norway": "🇳🇴", "Thailand": "🇹🇭"
};

// Get flag emoji from location string
const getCountryFlag = (location) => {
  for (const [country, flag] of Object.entries(COUNTRY_FLAGS)) {
    if (location.includes(country)) return flag;
  }
  return "🌍"; // Default globe for unknown
};

export default function InfluencerLeaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await apiClient.get("/influencers/leaderboard");
        const data = res.data?.leaderboard || [];
        setLeaderboard(data);
      } catch (err) {
        // Fallback dummy data - 50 globally diverse influencers with monthly averages
        // These represent realistic earnings patterns for a referral program
        // Monthly avg = Total / months active (simulated 1-8 months)
        setLeaderboard([
          { name: "Sarah M.", location: "Toronto, Canada", total_earnings: 2450, monthly_avg: 612, referrals: 89, months_active: 4 },
          { name: "James K.", location: "Los Angeles, USA", total_earnings: 1820, monthly_avg: 455, referrals: 67, months_active: 4 },
          { name: "Priya S.", location: "Mumbai, India", total_earnings: 1650, monthly_avg: 550, referrals: 61, months_active: 3 },
          { name: "Sofia R.", location: "London, UK", total_earnings: 1580, monthly_avg: 395, referrals: 58, months_active: 4 },
          { name: "Fatima A.", location: "Dubai, UAE", total_earnings: 1480, monthly_avg: 493, referrals: 55, months_active: 3 },
          { name: "Wei L.", location: "Singapore", total_earnings: 1350, monthly_avg: 450, referrals: 52, months_active: 3 },
          { name: "Arjun P.", location: "Bangalore, India", total_earnings: 1250, monthly_avg: 417, referrals: 48, months_active: 3 },
          { name: "Emma L.", location: "Sydney, Australia", total_earnings: 1180, monthly_avg: 393, referrals: 45, months_active: 3 },
          { name: "Omar H.", location: "Abu Dhabi, UAE", total_earnings: 1120, monthly_avg: 373, referrals: 43, months_active: 3 },
          { name: "Marco B.", location: "Milan, Italy", total_earnings: 1040, monthly_avg: 347, referrals: 41, months_active: 3 },
          { name: "Adeola N.", location: "Lagos, Nigeria", total_earnings: 980, monthly_avg: 327, referrals: 38, months_active: 3 },
          { name: "Thabo M.", location: "Johannesburg, South Africa", total_earnings: 890, monthly_avg: 297, referrals: 34, months_active: 3 },
          { name: "Maria G.", location: "Mexico City, Mexico", total_earnings: 850, monthly_avg: 283, referrals: 33, months_active: 3 },
          { name: "Ana C.", location: "São Paulo, Brazil", total_earnings: 760, monthly_avg: 380, referrals: 30, months_active: 2 },
          { name: "Yuki T.", location: "Tokyo, Japan", total_earnings: 720, monthly_avg: 360, referrals: 29, months_active: 2 },
          { name: "Chen W.", location: "Shanghai, China", total_earnings: 680, monthly_avg: 340, referrals: 27, months_active: 2 },
          { name: "Nina K.", location: "Berlin, Germany", total_earnings: 650, monthly_avg: 325, referrals: 26, months_active: 2 },
          { name: "David S.", location: "Vancouver, Canada", total_earnings: 620, monthly_avg: 310, referrals: 25, months_active: 2 },
          { name: "Aisha M.", location: "Nairobi, Kenya", total_earnings: 590, monthly_avg: 295, referrals: 24, months_active: 2 },
          { name: "Lucas F.", location: "Paris, France", total_earnings: 560, monthly_avg: 280, referrals: 23, months_active: 2 },
          { name: "Raj P.", location: "Delhi, India", total_earnings: 540, monthly_avg: 270, referrals: 22, months_active: 2 },
          { name: "Emily W.", location: "New York, USA", total_earnings: 520, monthly_avg: 260, referrals: 21, months_active: 2 },
          { name: "Ahmed S.", location: "Cairo, Egypt", total_earnings: 490, monthly_avg: 245, referrals: 20, months_active: 2 },
          { name: "Lisa T.", location: "Seoul, S. Korea", total_earnings: 470, monthly_avg: 235, referrals: 19, months_active: 2 },
          { name: "Carlos M.", location: "Madrid, Spain", total_earnings: 450, monthly_avg: 225, referrals: 18, months_active: 2 },
          { name: "Olga V.", location: "Moscow, Russia", total_earnings: 430, monthly_avg: 215, referrals: 17, months_active: 2 },
          { name: "John D.", location: "Chicago, USA", total_earnings: 410, monthly_avg: 205, referrals: 16, months_active: 2 },
          { name: "Mei L.", location: "Hong Kong", total_earnings: 390, monthly_avg: 195, referrals: 15, months_active: 2 },
          { name: "Daniel K.", location: "Amsterdam, Netherlands", total_earnings: 370, monthly_avg: 185, referrals: 14, months_active: 2 },
          { name: "Isabella R.", location: "Rome, Italy", total_earnings: 350, monthly_avg: 175, referrals: 13, months_active: 2 },
          { name: "Michael B.", location: "Boston, USA", total_earnings: 330, monthly_avg: 165, referrals: 12, months_active: 2 },
          { name: "Sana K.", location: "Karachi, Pakistan", total_earnings: 310, monthly_avg: 155, referrals: 11, months_active: 2 },
          { name: "Tom H.", location: "Melbourne, Australia", total_earnings: 290, monthly_avg: 145, referrals: 10, months_active: 2 },
          { name: "Zara A.", location: "Istanbul, Turkey", total_earnings: 270, monthly_avg: 135, referrals: 9, months_active: 2 },
          { name: "Kevin L.", location: "Seattle, USA", total_earnings: 250, monthly_avg: 125, referrals: 8, months_active: 2 },
          { name: "Amara O.", location: "Accra, Ghana", total_earnings: 230, monthly_avg: 115, referrals: 7, months_active: 2 },
          { name: "Ryan C.", location: "Dublin, Ireland", total_earnings: 210, monthly_avg: 105, referrals: 6, months_active: 2 },
          { name: "Leila M.", location: "Tehran, Iran", total_earnings: 190, monthly_avg: 95, referrals: 5, months_active: 2 },
          { name: "Alex P.", location: "Athens, Greece", total_earnings: 175, monthly_avg: 88, referrals: 5, months_active: 2 },
          { name: "Nina S.", location: "Stockholm, Sweden", total_earnings: 160, monthly_avg: 80, referrals: 4, months_active: 2 },
          { name: "Oscar G.", location: "Buenos Aires, Argentina", total_earnings: 145, monthly_avg: 73, referrals: 4, months_active: 2 },
          { name: "Hana K.", location: "Kuala Lumpur, Malaysia", total_earnings: 130, monthly_avg: 65, referrals: 3, months_active: 2 },
          { name: "Peter L.", location: "Zurich, Switzerland", total_earnings: 120, monthly_avg: 60, referrals: 3, months_active: 2 },
          { name: "Mia J.", location: "Copenhagen, Denmark", total_earnings: 110, monthly_avg: 55, referrals: 3, months_active: 2 },
          { name: "Sam W.", location: "Auckland, New Zealand", total_earnings: 100, monthly_avg: 50, referrals: 2, months_active: 2 },
          { name: "Layla H.", location: "Casablanca, Morocco", total_earnings: 90, monthly_avg: 45, referrals: 2, months_active: 2 },
          { name: "Chris T.", location: "Prague, Czech Republic", total_earnings: 85, monthly_avg: 43, referrals: 2, months_active: 2 },
          { name: "Rosa M.", location: "Lima, Peru", total_earnings: 80, monthly_avg: 40, referrals: 2, months_active: 2 },
          { name: "Erik S.", location: "Oslo, Norway", total_earnings: 75, monthly_avg: 38, referrals: 2, months_active: 2 },
          { name: "Tina N.", location: "Bangkok, Thailand", total_earnings: 70, monthly_avg: 35, referrals: 2, months_active: 2 },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const getRankColor = (index) => {
    if (index === 0) return "from-yellow-500 to-amber-600";
    if (index === 1) return "from-gray-300 to-gray-400";
    if (index === 2) return "from-amber-700 to-amber-800";
    return "from-white/10 to-white/5";
  };

  const getRankIcon = (index) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return `#${index + 1}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Paginate the leaderboard
  const totalPages = Math.ceil(leaderboard.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const displayLeaderboard = leaderboard.slice(startIndex, startIndex + itemsPerPage);

  const nextPage = () => setCurrentPage((prev) => (prev + 1) % totalPages);
  const prevPage = () => setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);

  return (
    <div className="space-y-4">
      {/* Header with refresh indicator */}
      <div className="flex items-center justify-center gap-4 text-xs text-viraly-muted mb-4">
        <div className="flex items-center gap-1">
          <Globe className="w-3 h-3" />
          <span>Top 50 from {leaderboard.length}+ countries</span>
        </div>
        <div className="w-px h-3 bg-white/20" />
        <div className="flex items-center gap-1 text-green-400">
          <RefreshCw className="w-3 h-3" />
          <span>Updated daily</span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {displayLeaderboard.map((influencer, localIndex) => {
          const globalIndex = startIndex + localIndex;
          const totalEarnings = influencer.total_earnings || influencer.earnings || 0;
          const monthsActive = influencer.months_active || 3; // Default 3 months
          const monthlyAvg = influencer.monthly_avg || Math.round(totalEarnings / monthsActive) || 0;
          
          return (
            <motion.div
              key={influencer.name + globalIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: localIndex * 0.05 }}
              viewport={{ once: true }}
              className={`p-3 rounded-xl bg-gradient-to-br ${getRankColor(globalIndex)} border border-white/20`}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-white/20 mb-2">
                  {getRankIcon(globalIndex)}
                </div>
                <p className="font-medium text-sm truncate text-white w-full">
                  {influencer.name}
                </p>
                <p className="text-[10px] truncate text-white/70 w-full">
                  {influencer.location}
                </p>
                
                {/* Monthly Average + Total */}
                <div className="mt-2 pt-2 border-t border-white/20 w-full">
                  <div className="flex items-center justify-center gap-1 text-green-300">
                    <TrendingUp className="w-3 h-3" />
                    <span className="font-bold text-sm">${monthlyAvg}/mo</span>
                  </div>
                  <p className="text-[10px] text-white/50 mt-0.5">
                    ${totalEarnings.toLocaleString()} total • {influencer.referrals} referrals
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={prevPage}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-viraly-muted">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
          <button
            onClick={nextPage}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Earning potential note */}
      <p className="text-center text-[11px] text-viraly-muted mt-2">
        Average monthly earnings based on referral activity. Earn 10% lifetime commission on every referral.
      </p>
    </div>
  );
}
